import React, { useEffect, useState } from "react";
import SchoolDatePicker from "../../components/SchoolDatePicker/SchoolDatePicker";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PageTitle from "../../components/PageTitle/PageTitle";
import Widget from "../../components/Widget/Widget";
import FoodMasterMenuService from "./Locality/Service/foodMasterMenuService";
import SchoolFoodMenuService from "./Locality/Service/schoolFoodMenuService";
import AddClassService from "./Locality/Service/addClassService";
import { showAppConfirm } from '../../context/AlertContext';
import { formatDate } from "../../utils/dateFormat";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#0f3b62ad",
    color: theme.palette.common.white,
  },
}))(TableCell);

const emptyForm = {
  foodItemNames: [],
  status: "Active",
  classWise: false,
  dayWise: false,
  dayName: "",
  classId: "",
  fromDate: "",
  toDate: "",
};

export default function FoodMaster() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [schoolFoodItemNames, setSchoolFoodItemNames] = useState([]);
  const [classList, setClassList] = useState([]);
  const dayOptions = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const schooleId = userDetails?.schooleId || "";
  const normalizeClassList = (value) => {
    const asArray = Array.isArray(value)
      ? value
      : Array.isArray(value?.data)
      ? value.data
      : Array.isArray(value?.classes)
      ? value.classes
      : [];

    const fallback = Array.isArray(userDetails?.classrooms) ? userDetails.classrooms : [];
    const source = asArray.length ? asArray : fallback;

    return source
      .map((c) => ({
        _id: c?._id || c?.id || c?.classId || "",
        className: c?.className || c?.name || "",
        status: c?.status,
      }))
      .filter((c) => c._id && c.className)
      .filter((c) => c.status !== false);
  };
  const load = async () => {
    const [masterList, classes, plannerList] = await Promise.all([
      FoodMasterMenuService.listBySchool(schooleId, true),
      AddClassService.getAllAddClass(schooleId, userDetails?.role, true),
      SchoolFoodMenuService.listBySchool(schooleId, false, true),
    ]);
    const safeMasterList = masterList || [];
    const safePlannerList = plannerList || [];
    setList(safeMasterList);
    setSchoolFoodItemNames(
      Array.from(
        new Set(
          safePlannerList
            .map((item) => String(item?.foodItemName || "").trim())
            .filter(Boolean)
        )
      )
    );
    setClassList(normalizeClassList(classes));
  };

  useEffect(() => {
    if (!schooleId) return;
    load().catch(() => {});
  }, [schooleId]);

  const onSave = async () => {
    const selectedNames = Array.isArray(form.foodItemNames) ? form.foodItemNames : [];
    if (!selectedNames.length) {
      window.alert("Select at least one food item.");
      return;
    }

    const body = {
      schooleId,
      itemNames: selectedNames,
      // keep a display/snapshot name for legacy UI
      foodItemName: selectedNames.join(", "),
      status: form.status || "Active",
      classWise: !!form.classWise,
      dayWise: !!form.dayWise,
      dayName: form.dayWise ? String(form.dayName || "").trim() : undefined,
      classId: form.classWise ? form.classId || undefined : undefined,
      // Important: send empty string on edit to clear dates in backend
      fromDate: editingId ? form.fromDate : form.fromDate || undefined,
      toDate: editingId ? form.toDate : form.toDate || undefined,
    };
    if (body.classWise && !body.classId) return;
    if (body.dayWise && !body.dayName) return;

    if (editingId) await FoodMasterMenuService.updateMenu(editingId, body);
    else await FoodMasterMenuService.addMenu(body);

    setOpen(false);
    setForm(emptyForm);
    setEditingId(null);
    load();
  };

  const onEdit = (item) => {
    setEditingId(item._id);
    setForm({
      foodItemNames: Array.isArray(item?.itemNames) && item.itemNames.length
        ? item.itemNames
        : item?.foodItemName
        ? String(item.foodItemName)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      status: item.status || "Active",
      classWise: !!item.classWise,
      dayWise: !!item.dayWise,
      dayName: item.dayName || "",
      classId: item.classId?._id || item.classId || "",
      fromDate: item.fromDate ? String(item.fromDate).slice(0, 10) : "",
      toDate: item.toDate ? String(item.toDate).slice(0, 10) : "",
    });
    setOpen(true);
  };

  const onDelete = async (item) => {
    if (!await showAppConfirm(`Delete "${item.foodItemName || "item"}"?`)) return;
    await FoodMasterMenuService.deleteMenu(item._id);
    load();
  };

  return (
    <>
      <PageTitle
        title="Meal schedule"
        button={
          <Button
            variant="contained"
            color="secondary"
            style={{ backgroundColor: "rgb(15 59 98)" }}
            onClick={() => {
              setEditingId(null);
              setForm(emptyForm);
              setOpen(true);
            }}
          >
            Add Meal Schedule
          </Button>
        }
      />

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget title="" upperTitle noBodyPadding>
            <Table className="dashboard-table-row-hover">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Food Item Name</StyledTableCell>
                  <StyledTableCell>Class Scope</StyledTableCell>
                  <StyledTableCell>Day</StyledTableCell>
                  <StyledTableCell>From Date</StyledTableCell>
                  <StyledTableCell>To Date</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Edit</StyledTableCell>
                  <StyledTableCell>Delete</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>
                      {Array.isArray(item?.itemNames) && item.itemNames.length
                        ? item.itemNames.join(", ")
                        : item.foodItemName || "-"}
                    </TableCell>
                    <TableCell>
                      {item.classWise
                        ? item.classId?.className || "Class-wise"
                        : item.dayWise
                        ? `Day-wise (${item.dayName || "-"})`
                        : "All classes"}
                    </TableCell>
                    <TableCell>{item.dayWise ? item.dayName || "-" : "-"}</TableCell>
                    <TableCell>{item.fromDate ? formatDate(item.fromDate) : "-"}</TableCell>
                    <TableCell>{item.toDate ? formatDate(item.toDate) : "-"}</TableCell>
                    <TableCell>{item.status || "-"}</TableCell>
                    <TableCell>
                      <EditIcon style={{ cursor: "pointer" }} onClick={() => onEdit(item)} />
                    </TableCell>
                    <TableCell>
                      <DeleteIcon style={{ cursor: "pointer" }} onClick={() => onDelete(item)} />
                    </TableCell>
                    
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Widget>
        </Grid>
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editingId ? "Edit Meal schedule" : "Add  Meal schedule"}</DialogTitle>
        <DialogContent style={{ width: 420 }}>
          <TextField
            select
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(", ") : ""),
            }}
            fullWidth
            margin="dense"
            label="Food Item Name (from school food menu)"
            value={form.foodItemNames}
            onChange={(e) => {
              const value = e?.target?.value || [];
              // MUI returns string | string[] for multi-select depending on autofill.
              const normalized = Array.isArray(value) ? value : String(value).split(",").map((s) => s.trim()).filter(Boolean);
              setForm((p) => ({ ...p, foodItemNames: normalized }));
            }}
          >
            {schoolFoodItemNames.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={form.foodItemNames.indexOf(name) > -1} color="primary" />
                <span style={{ marginLeft: 8 }}>{name}</span>
              </MenuItem>
            ))}
            {form.foodItemNames
              .filter((n) => n && !schoolFoodItemNames.includes(n))
              .map((n) => (
                <MenuItem key={n} value={n}>
                  <Checkbox checked color="primary" />
                  <span style={{ marginLeft: 8 }}>{n}</span>
                </MenuItem>
              ))}
          </TextField>

          <FormControlLabel
            style={{ marginTop: 8 }}
            control={
              <Checkbox
                checked={!!form.classWise}
                onChange={(e) => {
                  const checked = !!e?.target?.checked;
                  setForm((p) => ({ ...p, classWise: checked, classId: checked ? p.classId : "" }));
                }}
                color="primary"
              />
            }
            label="Class wise"
          />
          <FormControlLabel
            style={{ marginTop: 4 }}
            control={
              <Checkbox
                checked={!!form.dayWise}
                onChange={(e) => {
                  const checked = !!e?.target?.checked;
                  setForm((p) => ({ ...p, dayWise: checked, dayName: checked ? p.dayName : "" }));
                }}
                color="primary"
              />
            }
            label="Day wise"
          />

          {form.classWise ? (
            <TextField
              select
              fullWidth
              margin="dense"
              label="Class Name"
              value={form.classId}
              onChange={(e) => {
                const value = e?.target?.value || "";
                setForm((p) => ({ ...p, classId: value }));
              }}
            >
              {classList.length === 0 ? (
                <MenuItem value="" disabled>
                  No classes available
                </MenuItem>
              ) : null}
              {classList.map((cls) => (
                <MenuItem key={cls._id} value={cls._id}>
                  {cls.className}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {form.dayWise ? (
            <TextField
              select
              fullWidth
              margin="dense"
              label="Day Name"
              value={form.dayName}
              onChange={(e) => {
                const value = e?.target?.value || "";
                setForm((p) => ({ ...p, dayName: value }));
              }}
            >
              {dayOptions.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <SchoolDatePicker
            label="Start Date (optional)"
            value={form.fromDate}
            onChange={(iso) => {
              setForm((p) => ({ ...p, fromDate: iso }));
            }}
          />

          <SchoolDatePicker
            label="End Date (optional)"
            value={form.toDate}
            onChange={(iso) => {
              setForm((p) => ({ ...p, toDate: iso }));
            }}
          />

          <TextField
            select
            fullWidth
            margin="dense"
            label="Status"
            value={form.status}
            onChange={(e) => {
              const value = e?.target?.value || "Active";
              setForm((p) => ({ ...p, status: value }));
            }}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={onSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
